import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import {
  summarizeSystemHealth,
  uiVersion,
  type SystemTone,
} from '@/lib/system-status/system-status';
import { useSystemStatus } from '@/lib/system-status/use-system-status';

// Pinned to the bottom of the sidebar: the system's pulse in two quiet lines.
//
//   ● All systems operational        ← BFF /health verdict (hover for per-API detail)
//   UI 5fa0fef · API a1b2c3d         ← build SHAs of this bundle and the BFF
//
// Visible to every role on purpose — "is it me or is the system down?" is the
// first question support asks, and the answer should already be on screen.

const TONE_DOT: Record<SystemTone, string> = {
  ok: 'bg-emerald-500',
  degraded: 'bg-amber-500',
  offline: 'bg-rose-500',
  checking: 'bg-muted-foreground/40',
};

export function SidebarFooter() {
  const { t } = useTranslation('admin');
  const { data, isError } = useSystemStatus();
  const summary = summarizeSystemHealth(data, isError);
  const apiVersion = data?.version;

  return (
    <footer
      className="m-2 rounded-lg border border-border bg-card px-3 py-2.5 shadow-sm"
      data-testid="sidebar-footer"
      aria-label={t('systemStatus.label')}
    >
      <div
        className="flex items-center gap-1.5"
        role="status"
        title={summary.detail || undefined}
        data-testid="system-status"
        data-tone={summary.tone}
      >
        <span
          className={cn('h-2 w-2 shrink-0 rounded-full', TONE_DOT[summary.tone])}
          aria-hidden
        />
        <span className="truncate text-[11px] font-medium leading-4 text-muted-foreground">
          {summary.label}
        </span>
      </div>
      <p className="mt-1.5 truncate font-mono text-[10px] leading-4 text-muted-foreground/70">
        UI {uiVersion()}
        {apiVersion ? ` · API ${apiVersion}` : ''}
      </p>
    </footer>
  );
}
